import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Toast from '../../components/Toast';
import ConfirmModal from '../../components/ConfirmModal';
import OrganizationEditSidebar from '../../components/admin/OrganizationEditSidebar';
import { organizationApi } from '../../services/api';
import { getCountryFlag } from '../../utils/countryFlag';

export default function Organizations() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateSidebar, setShowCreateSidebar] = useState(false);
  const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] = useState(null);

  const role = localStorage.getItem('role');
  const isGlobalAdmin = role === 'global_admin';

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
  };

  const [selectedOrganization, setSelectedOrganization] = useState(null);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await organizationApi.getAll();
      setOrganizations(response.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      showToast('error', 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = () => {
    setSelectedOrganization(null);
    setShowCreateSidebar(true);
  };

  const handleEditOrganization = (organization) => {
    setSelectedOrganization(organization);
    setShowCreateSidebar(true);
  };

  const handleDeleteOrganization = (organization) => {
    setOrganizationToDelete(organization);
    setConfirmOpen(true);
  };

  const confirmDeleteOrganization = async () => {
    if (!organizationToDelete) return;
    try {
      await organizationApi.delete(organizationToDelete.id);
      showToast('success', 'Organization deleted successfully!');
      fetchOrganizations();
    } catch (error) {
      showToast('error', 'Failed to delete organization');
    } finally {
      setConfirmOpen(false);
      setOrganizationToDelete(null);
    }
  };

  return (
    <Layout>

      <Toast
        open={toast.open}
        setOpen={(open) => setToast(prev => ({ ...prev, open }))}
        type={toast.type}
        className="fixed top-6 right-6 z-[60]"
      >
        {toast.message}
      </Toast>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-eucnet-navy">Organization Management</h1>
          <p className="mt-2 text-eucnet-grey-600">
            Manage CSIRT organizations and their assessment engagements
          </p>
        </div>

        {/* Actions Bar */}
        <div className="mb-6 flex justify-between items-center">
          <div className="text-sm text-eucnet-grey-600">
            {organizations.length} organization{organizations.length !== 1 ? 's' : ''} found
          </div>
          {isGlobalAdmin && (
            <button
              onClick={handleCreateOrganization}
              className="bg-eucnet-blue hover:bg-eucnet-navy text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Create Organization
            </button>
          )}
        </div>

        {/* Organizations Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-eucnet-blue"></div>
          </div>
        ) : organizations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-eucnet-grey-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-eucnet-grey-900">No organizations</h3>
            <p className="mt-1 text-sm text-eucnet-grey-600">Get started by creating a new organization.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {organizations.map((organization) => (
              <div
                key={organization.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    {organization.logo && (
                      <div className="mb-3 flex justify-center">
                        <img
                          src={organization.logo.startsWith('data:') ? organization.logo : `data:image/jpeg;base64,${organization.logo}`}
                          alt={`${organization.name} logo`}
                          className="h-12 w-auto max-w-full object-contain"
                        />
                      </div>
                    )}
                    <h3 className="text-lg font-semibold text-eucnet-navy line-clamp-2">
                      {organization.name}
                    </h3>
                    {organization.short_name && (
                      <p className="text-sm text-eucnet-grey-600 mt-1">{organization.short_name}</p>
                    )}
                  </div>
                  {isGlobalAdmin && (
                    <div className="flex items-center gap-2 ml-2">
                      <button
                        onClick={() => handleEditOrganization(organization)}
                        className="text-eucnet-blue hover:text-eucnet-navy"
                        title="Edit organization"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteOrganization(organization)}
                        className="text-red-600 hover:text-red-700 pb-2"
                        title="Delete organization"
                      >
                        <svg className="w-8 h-8 fill-current" viewBox="0 -3 32 32">
                          <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
                          <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                
                {organization.description && (
                  <p className="text-sm text-eucnet-grey-600 mb-4 line-clamp-2">
                    {organization.description}
                  </p>
                )}

                <div className="space-y-2 text-sm">
                  {organization.country && (
                    <div className="flex items-center text-eucnet-grey-600">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="truncate">{organization.country}</span>
                      <span className="text-2xl ml-1 inline-block align-middle" aria-hidden="true">{getCountryFlag(organization.country)}</span>
                    </div>
                  )}
                  {organization.email && (
                    <div className="flex items-center text-eucnet-grey-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {organization.email}
                    </div>
                  )}
                  {organization.website && (
                    <div className="flex items-center text-eucnet-grey-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      <a href={organization.website} target="_blank" rel="noopener noreferrer" className="hover:text-eucnet-blue">
                        {organization.website}
                      </a>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-eucnet-grey-200">
                  <button
                    onClick={() => navigate(`/admin/organizations/${organization.id}`)}
                    className="w-full px-4 py-2 bg-eucnet-blue text-white rounded hover:bg-eucnet-navy text-sm font-medium transition-colors"
                  >
                    View Details →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <OrganizationEditSidebar
        isOpen={showCreateSidebar}
        onClose={() => {
          setShowCreateSidebar(false);
          setSelectedOrganization(null);
        }}
        organization={selectedOrganization}
        onSaved={fetchOrganizations}
        onNotify={showToast}
      />
      <ConfirmModal
        open={confirmOpen}
        title="Delete organization?"
        message={organizationToDelete ? `You are about to delete "${organizationToDelete.name}". This action cannot be undone.` : 'You are about to delete this organization.'}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteOrganization}
        onCancel={() => {
          setConfirmOpen(false);
          setOrganizationToDelete(null);
        }}
      />
    </Layout>
  );
}
